import { lazy, Suspense, useEffect, useMemo, useCallback, useSyncExternalStore } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import * as Sentry from "@sentry/react";
import { Toaster, toast } from "@/components/ui/sonner";
import { clearSessionToken, getSessionToken, roleHomeRoute, setSessionToken, api } from "@/lib/api";
import { getPushSubscriptionStatus, initPushNotifications, subscribeToPush } from "@/lib/push-notifications";
import { primeAudioAlerts } from "@/lib/audio-alerts";
import { ErrorBoundary } from "@/components/error-boundary";
import { PlanBlockedScreen } from "@/components/plan-blocked-screen";
import "@/App.css";

const SESSION_SNAPSHOT_KEY = "hosthelp.session.snapshot";
const PUSH_REMINDER_PREFIX = "hosthelp.push.reminded.";
const EMPTY_SESSION = { token: "", user: null, hotel: null };

const authStore = (() => {
  let value = { session: EMPTY_SESSION, loading: true };
  const listeners = new Set();
  return {
    getSnapshot: () => value,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    publish: (next) => {
      value = { ...value, ...next };
      listeners.forEach((listener) => listener());
    },
  };
})();

const bootstrapSession = () => {
  const token = getSessionToken();
  if (!token) {
    authStore.publish({ session: EMPTY_SESSION, loading: false });
    return;
  }
  api.get("/auth/me").then((response) => {
    const nextSession = { token, user: response.data.user, hotel: response.data.hotel };
    authStore.publish({ session: nextSession, loading: false });
    window.localStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify({ user: response.data.user, hotel: response.data.hotel }));
  }).catch((error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      clearSessionToken();
      window.localStorage.removeItem(SESSION_SNAPSHOT_KEY);
      authStore.publish({ session: EMPTY_SESSION, loading: false });
      return;
    }
    try {
      const snapshot = JSON.parse(window.localStorage.getItem(SESSION_SNAPSHOT_KEY) || "null");
      if (snapshot?.user) {
        authStore.publish({ session: { token, user: snapshot.user, hotel: snapshot.hotel || null }, loading: false });
        return;
      }
    } catch {
      // ignore broken cache
    }
    authStore.publish({ session: EMPTY_SESSION, loading: false });
  });
};

bootstrapSession();

/* ── Code-split all page routes for smaller initial bundle ── */
const LandingPage = lazy(() => import("@/pages/landing-page"));
const LoginPage = lazy(() => import("@/pages/login-page"));
const SuperAdminPage = lazy(() => import("@/pages/super-admin-page"));
const OwnerPage = lazy(() => import("@/pages/owner-page"));
const StaffPage = lazy(() => import("@/pages/staff-page"));
const GuestRequestPage = lazy(() => import("@/pages/guest-request-page"));
const GuestChatPortal = lazy(() => import("@/pages/guest-chat-portal"));
const GuestStatusPage = lazy(() => import("@/pages/guest-status-page"));
const BlogsPage = lazy(() => import("@/pages/blogs-page"));
const BlogPostPage = lazy(() => import("@/pages/blog-post-page"));
const ContactPage = lazy(() => import("@/pages/contact-page"));
const PrivacyPolicyPage = lazy(() => import("@/pages/privacy-policy-page"));
const OwnerSetupPage = lazy(() => import("@/pages/owner-setup-page"));
const PricingPage = lazy(() => import("@/pages/pricing-page"));
const ForHotelsPage = lazy(() => import("@/pages/for-hotels-page"));
const ForMultiPropertyPage = lazy(() => import("@/pages/for-multi-property-page"));
const ForBnBPage = lazy(() => import("@/pages/for-bnb-page"));
const CareersPage = lazy(() => import("@/pages/careers-page"));
const CareerPostPage = lazy(() => import("@/pages/career-post-page"));
const PropertySetupPage = lazy(() => import("@/pages/property-setup-page"));
const FeatureSeoPage = lazy(() => import("@/pages/seo-landing-page").then((m) => ({ default: m.FeatureSeoPage })));
const CitySeoPage = lazy(() => import("@/pages/seo-landing-page").then((m) => ({ default: m.CitySeoPage })));
const CountrySeoPage = lazy(() => import("@/pages/seo-landing-page").then((m) => ({ default: m.CountrySeoPage })));
const CompetitorSeoPage = lazy(() => import("@/pages/seo-landing-page").then((m) => ({ default: m.CompetitorSeoPage })));

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-[#FF9500]" />
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const ProtectedRoute = ({ session, loading, allowedRoles, children, onLogout }) => {
  if (loading) return <PageLoader />;
  if (!session?.token || !session?.user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(session.user.role)) return <Navigate to={roleHomeRoute(session.user.role)} replace />;
  /* Block owner/staff if hotel plan is not active */
  const planStatus = session.hotel?.plan_status;
  if (session.user.role !== "super_admin" && planStatus && planStatus !== "active") {
    return <PlanBlockedScreen planStatus={planStatus} onLogout={onLogout} />;
  }
  return children;
};

function App() {
  const authSnapshot = useSyncExternalStore(authStore.subscribe, authStore.getSnapshot, authStore.getSnapshot);
  const session = authSnapshot.session;
  const loadingSession = authSnapshot.loading;

  useEffect(() => { initPushNotifications(); }, []);
  useEffect(() => { primeAudioAlerts(); }, []);

  // Global scroll-reveal observer — any element with class `hh-reveal` slides
  // + fades in on first viewport entry. One observer, lazy-mounted, idempotent.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const seen = new WeakSet();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !seen.has(entry.target)) {
          entry.target.classList.add("hh-reveal-in");
          seen.add(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -10% 0px", threshold: 0.08 });

    const rescan = () => {
      document.querySelectorAll(".hh-reveal:not(.hh-reveal-in)").forEach((el) => {
        if (!seen.has(el)) io.observe(el);
      });
    };
    rescan();
    // Re-scan on route changes (DOM nodes change)
    const mo = new MutationObserver(rescan);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, []);

  useEffect(() => {
    if (loadingSession || !session?.user || !['staff', 'owner'].includes(session.user.role)) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const status = await getPushSubscriptionStatus();
        if (cancelled || !status.supported) return;
        if (status.permission === 'granted') {
          if (!status.subscribed) await subscribeToPush(session.user.role);
          return;
        }
        const today = new Date().toISOString().slice(0, 10);
        const reminderKey = `${PUSH_REMINDER_PREFIX}${session.user.email || session.user.id || 'user'}`;
        if (window.localStorage.getItem(reminderKey) === today) return;
        window.localStorage.setItem(reminderKey, today);
        toast("Enable background ticket alerts", {
          description: "Tap once so new requests notify even when the app is not open.",
          duration: 12000,
          action: {
            label: "Enable",
            onClick: () => subscribeToPush(session.user.role).catch(() => null),
          },
        });
      } catch {
        // keep notification reminder non-blocking
      }
    }, 1800);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [loadingSession, session?.user]);

  const handleAuthSuccess = (payload) => {
    setSessionToken(payload.access_token);
    authStore.publish({ session: { token: payload.access_token, user: payload.user, hotel: payload.hotel }, loading: false });
    window.localStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify({ user: payload.user, hotel: payload.hotel }));
    Sentry.setUser({ id: payload.user?.email, email: payload.user?.email, username: payload.user?.name });
    if (payload.is_new_owner) {
      window.localStorage.setItem("hosthelp.needs_setup", "true");
      window.localStorage.setItem("hosthelp.needs_property_setup", "true");
    }
  };
  const handleLogout = () => { clearSessionToken(); window.localStorage.removeItem(SESSION_SNAPSHOT_KEY); authStore.publish({ session: EMPTY_SESSION, loading: false }); Sentry.setUser(null); };
  const memoSession = useMemo(() => session, [session]);

  return (
    <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage session={memoSession} />} />
          <Route path="/login" element={memoSession?.user ? <Navigate to={window.localStorage.getItem("hosthelp.needs_property_setup") === "true" ? "/register/setup" : window.localStorage.getItem("hosthelp.needs_setup") === "true" ? "/owner/setup" : roleHomeRoute(memoSession.user.role)} replace /> : <LoginPage onAuthSuccess={handleAuthSuccess} />} />
          <Route path="/blogs" element={<BlogsPage />} />
          <Route path="/blogs/:slug" element={<BlogPostPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/for-hotels" element={<ForHotelsPage />} />
          <Route path="/for-multi-property" element={<ForMultiPropertyPage />} />
          <Route path="/for-bnb-apartments" element={<ForBnBPage />} />
          <Route path="/ai-hotel-concierge" element={<FeatureSeoPage />} />
          <Route path="/qr-guest-portal" element={<FeatureSeoPage />} />
          <Route path="/hotel-ticket-management" element={<FeatureSeoPage />} />
          <Route path="/hotel-concierge-software/:citySlug" element={<CitySeoPage />} />
          <Route path="/ai-hotel-concierge/:countrySlug" element={<CountrySeoPage />} />
          <Route path="/compare/:competitorSlug" element={<CompetitorSeoPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/:slug" element={<CareerPostPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/guest/:hotelId/:roomNumber" element={<GuestChatPortal />} />
          <Route path="/guest/facility/:hotelId/:facilityId" element={<GuestChatPortal />} />
          <Route path="/guest/ticket/:ticketId" element={<GuestStatusPage />} />
          <Route path="/guest/status/:ticketId" element={<GuestStatusPage />} />
          <Route path="/super-admin" element={<ProtectedRoute session={memoSession} loading={loadingSession} allowedRoles={["super_admin"]} onLogout={handleLogout}><SuperAdminPage session={memoSession} onLogout={handleLogout} /></ProtectedRoute>} />
          <Route path="/owner/setup" element={<ProtectedRoute session={memoSession} loading={loadingSession} allowedRoles={["owner"]} onLogout={handleLogout}><OwnerSetupPage session={memoSession} onSetupComplete={() => window.localStorage.removeItem("hosthelp.needs_setup")} /></ProtectedRoute>} />
          <Route path="/register/setup" element={<ProtectedRoute session={memoSession} loading={loadingSession} allowedRoles={["owner"]} onLogout={handleLogout}><PropertySetupPage session={memoSession} onComplete={() => { window.localStorage.removeItem("hosthelp.needs_property_setup"); window.localStorage.removeItem("hosthelp.needs_setup"); }} /></ProtectedRoute>} />
          <Route path="/owner" element={
            <ProtectedRoute session={memoSession} loading={loadingSession} allowedRoles={["owner"]} onLogout={handleLogout}>
              {!memoSession?.hotel?.property_type
                ? <Navigate to="/register/setup" replace />
                : <OwnerPage session={memoSession} onLogout={handleLogout} />}
            </ProtectedRoute>
          } />
          <Route path="/staff" element={<ProtectedRoute session={memoSession} loading={loadingSession} allowedRoles={["staff"]} onLogout={handleLogout}><StaffPage session={memoSession} onLogout={handleLogout} /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
